/**
 * @fileoverview アプリケーション全体で使用する定数を管理します。
 * CSSクラス名、DOM要素のID、外部ライブラリの初期化など、
 * 静的な値を一元管理することで、保守性と一貫性を高めます。
 */
"use strict";

export const Constants = {
  /**
   * @description アプリケーションの動作設定。
   * ビルド時に `dist/config.js` から `serverConfig` という変数名で動的に読み込まれます。
   */
  APP_CONFIG: serverConfig,

  /**
   * @description UIの状態やアニメーションを制御するためのCSSクラス名。
   */
  CSS_CLASSES: {
    HIDDEN: "hidden", // 要素を非表示にする
    CORRECT_FEEDBACK: "correct", // 正解時のフィードバック
    INCORRECT_FEEDBACK: "incorrect", // 不正解時のフィードバック
    CORRECT_ANSWER_ITEM: "correct-answer", // 解答後の正しい選択肢
    SELECTED_INCORRECT_ITEM: "selected-incorrect", // 解答後の選択した誤った選択肢
    OPTION_SPECIFIC_EXPLANATION: "option-specific-explanation", // 選択肢ごとの解説
    DISABLED_OPTION: "disabled-option", // 無効化された選択肢
    ANIMATED: "animated", // アニメーション基本クラス
    FADE_IN: "fade-in", // フェードインアニメーション
    QUESTION_ELEMENT_APPEAR: "question-element-appear", // 問題要素の出現アニメーション
    OPTION_ITEM_APPEAR: "option-item-appear", // 選択肢の出現アニメーション
    FEEDBACK_POP_IN: "feedback-pop-in", // フィードバックのポップインアニメーション
    ACCORDION_HEADER_ACTIVE: "active", // 開いているアコーディオンヘッダー
    ACCORDION_PANEL_ACTIVE: "active", // 開いているアコーディオンパネル
  },

  /**
   * @description JavaScriptからDOMを操作するための要素ID。
   */
  ELEMENT_IDS: {
    // 全体・エリア
    LOADING_INDICATOR: "loading-indicator",
    LOGIN_AREA: "login-area",
    GOOGLE_LOGIN_BUTTON: "google-login-button",
    LOGGED_IN_VIEW: "logged-in-view",
    EXAM_SELECTION_AREA: "exam-selection-area",
    QUIZ_AREA: "quiz-area",
    RESULT_AREA: "result-area",

    // 試験選択画面
    QUIZ_LENGTH_OPTIONS_CONTAINER: "quiz-length-options-container",
    QUIZ_LENGTH_SELECT: "quiz-length-select",
    QUIZ_ALGORITHM_OPTIONS_CONTAINER: "quiz-algorithm-options-container",
    QUIZ_ALGORITHM_SELECT: "quiz-algorithm-select",
    QUIZ_MODE_OPTIONS_CONTAINER: "quiz-mode-options-container",
    QUIZ_MODE_SELECT: "quiz-mode-select",
    EXAM_ACCORDION_CONTAINER: "exam-accordion-container",

    // クイズ画面
    TOP_PROGRESS_BAR: "top-progress-bar",
    BOTTOM_PROGRESS_BAR: "bottom-progress-bar",
    PROGRESS_TEXT: "progress-text",
    QUESTION_INFO_DISPLAY: "question-info-display",
    QUESTION_INFO_AREA: "question-info-area",
    QUESTION_INFO_DIFFICULTY: "question-info-difficulty",
    QUESTION_INFO_IMPORTANCE: "question-info-importance",
    QUESTION_INFO_TAGS_CONTAINER: "question-info-tags-container",
    QUESTION_TEXT: "question-text",
    OPTIONS_LIST: "options-list",
    FEEDBACK: "feedback",
    ANSWER_BTN: "answer-btn",
    OVERALL_EXPLANATION_CONTAINER: "overall-explanation-container",
    OVERALL_EXPLANATION: "overall-explanation",
    NEXT_QUESTION_BTN: "next-question-btn",
    SHOW_RESULT_BTN: "show-result-btn",

    // 結果画面
    SCORE_TEXT: "score-text",
    ACCURACY_TEXT: "accuracy-text",
    RESTART_QUIZ_BTN: "restart-quiz-btn",
    RESULT_CHART: "resultChart",
    AREA_RESULT_CHART: "areaResultChart",
    AREA_CHART_CONTAINER: "area-chart-container",

    // カスタムダイアログ
    CUSTOM_CONFIRM_DIALOG: "custom-confirm-dialog",
    CUSTOM_CONFIRM_DIALOG_CONTENT: "custom-dialog-content",
    CUSTOM_CONFIRM_TITLE: "custom-confirm-title",
    CUSTOM_DIALOG_BODY: "custom-dialog-body",
    CUSTOM_CONFIRM_CANCEL_BTN: "custom-confirm-cancel-btn",
    CUSTOM_CONFIRM_OK_BTN: "custom-confirm-ok-btn",

    // 設定メニュー
    SETTINGS_BUTTON: "settings-button",
    SETTINGS_MENU: "settings-menu",
    MENU_USER_EMAIL: "menu-user-email",
    EDIT_QUESTION_MENU_ITEM: "edit-question-menu-item",
    LOGOUT_BUTTON: "logout-button",
  },

  /**
   * @description 解答結果を示す内部的な識別子。
   */
  ANSWER_RESULTS: {
    CORRECT: "correct",
    INCORRECT: "incorrect",
  },

  /**
   * @description 選択肢の前に表示するプレフィックス (A, B, C...)
   */
  OPTION_PREFIXES: ["A", "B", "C", "D", "E", "F"],
};

/**
 * Supabaseクライアントのインスタンスを生成・初期化します。
 * この `supabaseClient` は、ApiモジュールやAppモジュールから利用されます。
 */
export const supabaseClient = window.supabase.createClient(
  `https://${Constants.APP_CONFIG.SUPABASE_URL}`,
  Constants.APP_CONFIG.SUPABASE_ANON_KEY as string
);
